import type { BudgetCategoryData, BudgetYearData } from './useBudget'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type DependencyGraph = Record<number, number[]>

// ─────────────────────────────────────────────────────────────────────────────
// Safe Math / Logic Formula Evaluator (XSS & CSP Safe replacement for new Function)
// ─────────────────────────────────────────────────────────────────────────────

interface Token {
  type: 'NUMBER' | 'BOOLEAN' | 'OP' | 'LPAREN' | 'RPAREN' | 'TERNARY' | 'COLON' | 'EOF'
  value: string
}

function tokenize(expr: string): Token[] {
  const tokens: Token[] = []
  let i = 0

  while (i < expr.length) {
    const char = expr[i]

    if (/\s/.test(char)) {
      i++
      continue
    }

    // Check 3-character operators
    if (expr.substring(i, i + 3) === '===') {
      tokens.push({ type: 'OP', value: '==' })
      i += 3
      continue
    }
    if (expr.substring(i, i + 3) === '!==') {
      tokens.push({ type: 'OP', value: '!=' })
      i += 3
      continue
    }

    // Check 2-character operators
    if (expr.substring(i, i + 2) === '&&') {
      tokens.push({ type: 'OP', value: '&&' })
      i += 2
      continue
    }
    if (expr.substring(i, i + 2) === '||') {
      tokens.push({ type: 'OP', value: '||' })
      i += 2
      continue
    }
    if (expr.substring(i, i + 2) === '==') {
      tokens.push({ type: 'OP', value: '==' })
      i += 2
      continue
    }
    if (expr.substring(i, i + 2) === '!=') {
      tokens.push({ type: 'OP', value: '!=' })
      i += 2
      continue
    }
    if (expr.substring(i, i + 2) === '<=') {
      tokens.push({ type: 'OP', value: '<=' })
      i += 2
      continue
    }
    if (expr.substring(i, i + 2) === '>=') {
      tokens.push({ type: 'OP', value: '>=' })
      i += 2
      continue
    }

    // Single-character tokens
    if (char === '(') {
      tokens.push({ type: 'LPAREN', value: '(' })
      i++
      continue
    }
    if (char === ')') {
      tokens.push({ type: 'RPAREN', value: ')' })
      i++
      continue
    }
    if (char === '?') {
      tokens.push({ type: 'TERNARY', value: '?' })
      i++
      continue
    }
    if (char === ':') {
      tokens.push({ type: 'COLON', value: ':' })
      i++
      continue
    }

    if ('+-*/<>!'.includes(char)) {
      tokens.push({ type: 'OP', value: char })
      i++
      continue
    }

    // Numbers
    if (/\d/.test(char) || char === '.') {
      let numStr = ''
      while (i < expr.length && (/[\d.]/.test(expr[i]))) {
        numStr += expr[i]
        i++
      }
      tokens.push({ type: 'NUMBER', value: numStr })
      continue
    }

    // Identifiers/keywords like true/false
    if (/[a-zA-Z]/.test(char)) {
      let wordStr = ''
      while (i < expr.length && /[a-zA-Z0-9_]/.test(expr[i])) {
        wordStr += expr[i]
        i++
      }
      const lower = wordStr.toLowerCase()
      if (lower === 'true') {
        tokens.push({ type: 'BOOLEAN', value: 'true' })
        continue
      }
      if (lower === 'false') {
        tokens.push({ type: 'BOOLEAN', value: 'false' })
        continue
      }
      throw new Error(`Unknown identifier: ${wordStr}`)
    }

    throw new Error(`Unexpected character: ${char}`)
  }

  tokens.push({ type: 'EOF', value: '' })
  return tokens
}

type ASTNode =
  | { type: 'NUMBER'; value: number }
  | { type: 'BOOLEAN'; value: boolean }
  | { type: 'UNARY_OP'; op: string; expr: ASTNode }
  | { type: 'BINARY_OP'; op: string; left: ASTNode; right: ASTNode }
  | { type: 'TERNARY'; cond: ASTNode; left: ASTNode; right: ASTNode }

class FormulaParser {
  private tokens: Token[]
  private index = 0

  constructor(tokens: Token[]) {
    this.tokens = tokens
  }

  private peek(): Token {
    return this.tokens[this.index]
  }

  private consume(expectedType?: string): Token {
    const token = this.peek()
    if (expectedType && token.type !== expectedType) {
      throw new Error(`Expected token type ${expectedType}, got ${token.type}`)
    }
    this.index++
    return token
  }

  public parseExpression(): ASTNode {
    return this.parseConditional()
  }

  // Conditional -> OrExpression ('?' Expression ':' Expression)?
  private parseConditional(): ASTNode {
    const expr = this.parseOr()
    if (this.peek().type === 'TERNARY') {
      this.consume('TERNARY')
      const left = this.parseExpression()
      this.consume('COLON')
      const right = this.parseExpression()
      return { type: 'TERNARY', cond: expr, left, right }
    }
    return expr
  }

  // OrExpression -> AndExpression ('||' AndExpression)*
  private parseOr(): ASTNode {
    let val = this.parseAnd()
    while (this.peek().type === 'OP' && this.peek().value === '||') {
      this.consume()
      const next = this.parseAnd()
      val = { type: 'BINARY_OP', op: '||', left: val, right: next }
    }
    return val
  }

  // AndExpression -> EqualityExpression ('&&' EqualityExpression)*
  private parseAnd(): ASTNode {
    let val = this.parseEquality()
    while (this.peek().type === 'OP' && this.peek().value === '&&') {
      this.consume()
      const next = this.parseEquality()
      val = { type: 'BINARY_OP', op: '&&', left: val, right: next }
    }
    return val
  }

  // EqualityExpression -> RelationalExpression (('==' | '!=') RelationalExpression)*
  private parseEquality(): ASTNode {
    let val = this.parseRelational()
    while (this.peek().type === 'OP' && (this.peek().value === '==' || this.peek().value === '!=')) {
      const op = this.consume().value
      const next = this.parseRelational()
      val = { type: 'BINARY_OP', op, left: val, right: next }
    }
    return val
  }

  // RelationalExpression -> AdditiveExpression (('<' | '>' | '<=' | '>=') AdditiveExpression)*
  private parseRelational(): ASTNode {
    let val = this.parseAdditive()
    while (
      this.peek().type === 'OP' &&
      (this.peek().value === '<' || this.peek().value === '>' || this.peek().value === '<=' || this.peek().value === '>=')
    ) {
      const op = this.consume().value
      const next = this.parseAdditive()
      val = { type: 'BINARY_OP', op, left: val, right: next }
    }
    return val
  }

  // AdditiveExpression -> MultiplicativeExpression (('+' | '-') MultiplicativeExpression)*
  private parseAdditive(): ASTNode {
    let val = this.parseMultiplicative()
    while (this.peek().type === 'OP' && (this.peek().value === '+' || this.peek().value === '-')) {
      const op = this.consume().value
      const next = this.parseMultiplicative()
      val = { type: 'BINARY_OP', op, left: val, right: next }
    }
    return val
  }

  // MultiplicativeExpression -> UnaryExpression (('*' | '/') UnaryExpression)*
  private parseMultiplicative(): ASTNode {
    let val = this.parseUnary()
    while (this.peek().type === 'OP' && (this.peek().value === '*' || this.peek().value === '/')) {
      const op = this.consume().value
      const next = this.parseUnary()
      val = { type: 'BINARY_OP', op, left: val, right: next }
    }
    return val
  }

  // UnaryExpression -> ('+' | '-' | '!')? PrimaryExpression
  private parseUnary(): ASTNode {
    const token = this.peek()
    if (token.type === 'OP' && (token.value === '+' || token.value === '-' || token.value === '!')) {
      this.consume()
      const val = this.parseUnary()
      return { type: 'UNARY_OP', op: token.value, expr: val }
    }
    return this.parsePrimary()
  }

  // PrimaryExpression -> Number | Boolean | '(' Expression ')'
  private parsePrimary(): ASTNode {
    const token = this.peek()
    if (token.type === 'NUMBER') {
      this.consume()
      const val = Number(token.value)
      if (isNaN(val)) throw new Error(`Invalid number: ${token.value}`)
      return { type: 'NUMBER', value: val }
    }
    if (token.type === 'BOOLEAN') {
      this.consume()
      return { type: 'BOOLEAN', value: token.value === 'true' }
    }
    if (token.type === 'LPAREN') {
      this.consume('LPAREN')
      const val = this.parseExpression()
      this.consume('RPAREN')
      return val
    }
    throw new Error(`Unexpected token: ${token.value}`)
  }
}

function evaluateAST(node: ASTNode): any {
  switch (node.type) {
    case 'NUMBER': return node.value
    case 'BOOLEAN': return node.value
    case 'UNARY_OP': {
      const val = evaluateAST(node.expr)
      if (node.op === '+') return +val
      if (node.op === '-') return -val
      if (node.op === '!') return !val
      throw new Error(`Unknown unary op: ${node.op}`)
    }
    case 'BINARY_OP': {
      // Lazy evaluation / Short-circuiting for logical operators
      if (node.op === '&&') {
        return evaluateAST(node.left) && evaluateAST(node.right)
      }
      if (node.op === '||') {
        return evaluateAST(node.left) || evaluateAST(node.right)
      }

      const leftVal = evaluateAST(node.left)
      const rightVal = evaluateAST(node.right)
      switch (node.op) {
        case '+': return leftVal + rightVal
        case '-': return leftVal - rightVal
        case '*': return leftVal * rightVal
        case '/':
          if (rightVal === 0) throw new Error('Division by zero')
          return leftVal / rightVal
        case '==': return leftVal == rightVal
        case '!=': return leftVal != rightVal
        case '<': return leftVal < rightVal
        case '>': return leftVal > rightVal
        case '<=': return leftVal <= rightVal
        case '>=': return leftVal >= rightVal
        default: throw new Error(`Unknown binary op: ${node.op}`)
      }
    }
    case 'TERNARY': {
      // Lazy evaluation / Short-circuiting: only evaluate the chosen branch
      const condVal = evaluateAST(node.cond)
      return condVal ? evaluateAST(node.left) : evaluateAST(node.right)
    }
  }
}

export function safeEval(expr: string): number {
  const tokens = tokenize(expr)
  const parser = new FormulaParser(tokens)
  const ast = parser.parseExpression()
  if (parser.peek().type !== 'EOF') {
    throw new Error('Extra tokens after expression')
  }
  const result = evaluateAST(ast)
  if (typeof result === 'boolean') {
    return result ? 1 : 0
  }
  if (typeof result !== 'number' || !isFinite(result)) {
    throw new Error('Result is not a finite number')
  }
  return result
}

// ─────────────────────────────────────────────────────────────────────────────
// Composable
// ─────────────────────────────────────────────────────────────────────────────

import { calculateIncomeTaxSync, calculateCorporateTaxSync } from './useUrssafApi'
import { useBudget } from './useBudget'

export function useBudgetFormula() {
  const { taxStrategies } = useBudget()

  /**
   * Extracts all {cat:ID} or {cat:ID:-N} references from a formula string.
   */
  function extractCategoryIds(formula: string): number[] {
    const regex = /\{cat:(\d+)(?::-\d+)?\}/g
    const ids: number[] = []
    let match: RegExpExecArray | null
    while ((match = regex.exec(formula)) !== null) {
      ids.push(parseInt(match[1], 10))
    }
    return ids
  }

  /**
   * Extracts only same-month {cat:ID} references (ignoring :-N offsets).
   * Used for cycle detection, as past references don't create synchronous cycles.
   */
  function extractSameMonthCategoryIds(formula: string): number[] {
    const regex = /\{cat:(\d+)\}/g
    const ids: number[] = []
    let match: RegExpExecArray | null
    while ((match = regex.exec(formula)) !== null) {
      ids.push(parseInt(match[1], 10))
    }
    return ids
  }

  /**
   * Builds a dependency graph: { categoryId: [referencedIds] }
   * Only includes categories that have formulas, and ONLY same-month references.
   */
  function buildDependencyGraph(categories: BudgetCategoryData[]): DependencyGraph {
    const graph: DependencyGraph = {}
    for (const cat of categories) {
      graph[cat.id] = cat.formula ? extractSameMonthCategoryIds(cat.formula) : []
    }
    return graph
  }

  /**
   * Performs a topological sort using Kahn's algorithm.
   * Returns ordered array of category IDs, or null if a cycle is detected.
   */
  function topologicalSort(categories: BudgetCategoryData[], graph: DependencyGraph): number[] | null {
    const inDegree: Record<number, number> = {}
    const reverseGraph: Record<number, number[]> = {}

    for (const cat of categories) {
      inDegree[cat.id]    = 0
      reverseGraph[cat.id] = []
    }

    for (const [nodeId, deps] of Object.entries(graph)) {
      for (const depId of deps) {
        if (inDegree[depId] === undefined) continue // external / unknown reference
        inDegree[nodeId] = (inDegree[nodeId] ?? 0) + 1
        reverseGraph[depId].push(parseInt(nodeId, 10))
      }
    }

    // Start with all nodes having inDegree = 0
    const queue: number[] = []
    for (const cat of categories) {
      if ((inDegree[cat.id] ?? 0) === 0) {
        queue.push(cat.id)
      }
    }

    const sorted: number[] = []
    while (queue.length > 0) {
      const node = queue.shift()!
      sorted.push(node)
      for (const dependent of (reverseGraph[node] ?? [])) {
        inDegree[dependent]--
        if (inDegree[dependent] === 0) {
          queue.push(dependent)
        }
      }
    }

    // If not all nodes are in sorted, there's a cycle
    return sorted.length === categories.length ? sorted : null
  }

  /**
   * Evaluates a single formula.
   * getResolvedValue(catId, offset) provides the data (offset <= 0).
   */
  function evaluateFormula(
    formula: string,
    getResolvedValue: (catId: number, offset: number) => number | null,
    getResteValue: (type: 'urssaf' | 'ir' | 'is', yearOffset: number) => number,
    contextVariables: Record<string, number> = {},
  ): number | null {
    // Replace custom context variables first
    let expr = formula
    for (const [key, val] of Object.entries(contextVariables)) {
      expr = expr.replace(new RegExp(`\\{${key}\\}`, 'gi'), String(val))
    }

    // Replace {RESTE_URSSAF} or {RESTE_URSSAF:-N}
    expr = expr.replace(/\{RESTE_URSSAF(?::\s*-(\d+))?\}/gi, (_, offsetStr) => {
      const offset = offsetStr ? -parseInt(offsetStr, 10) : 0
      return String(getResteValue('urssaf', offset))
    })

    // Replace {RESTE_IR} or {RESTE_IR:-N}
    expr = expr.replace(/\{RESTE_IR(?::\s*-(\d+))?\}/gi, (_, offsetStr) => {
      const offset = offsetStr ? -parseInt(offsetStr, 10) : 0
      return String(getResteValue('ir', offset))
    })

    // Replace {RESTE_IS} or {RESTE_IS:-N}
    expr = expr.replace(/\{RESTE_IS(?::\s*-(\d+))?\}/gi, (_, offsetStr) => {
      const offset = offsetStr ? -parseInt(offsetStr, 10) : 0
      return String(getResteValue('is', offset))
    })

    // Replace all {cat:ID} or {cat:ID:-N} placeholders
    expr = expr.replace(/\{cat:(\d+)(?::\s*-(\d+))?\}/gi, (_, idStr, offsetStr) => {
      const id     = parseInt(idStr, 10)
      const offset = offsetStr ? -parseInt(offsetStr, 10) : 0
      const val    = getResolvedValue(id, offset)
      if (val === null) return '0'
      return String(val)
    })

    try {
      const result = safeEval(expr)
      return Math.round(result * 100) / 100
    } catch {
      return null
    }
  }

  /**
   * Resolves formulas for ALL loaded years sequentially.
   */
  function resolveAllYears(budgetDataByYear: Record<number, BudgetYearData>): Record<number, ResolvedBudgetData> {
    const years = Object.keys(budgetDataByYear).map(Number).sort((a, b) => a - b)
    const resolvedByYear: Record<number, ResolvedBudgetData> = {}

    for (const year of years) {
      resolvedByYear[year] = _resolveFormulasForYear(year, budgetDataByYear[year], resolvedByYear)
    }

    return resolvedByYear
  }

  function _resolveFormulasForYear(
    year: number,
    data: BudgetYearData,
    resolvedByYear: Record<number, ResolvedBudgetData>,
  ): ResolvedBudgetData {
    const allCategories = [
      ...data.personal.categories,
      ...data.professional.categories,
    ]

    const graph  = buildDependencyGraph(allCategories)
    const sorted = topologicalSort(allCategories, graph)

    const catIndex: Record<number, BudgetCategoryData> = {}
    for (const cat of allCategories) catIndex[cat.id] = cat

    const resolvedMonths: Record<number, Record<number, number | null>> = {}
    const evalOrder = sorted ?? allCategories.map(c => c.id)

    for (const catId of evalOrder) {
      resolvedMonths[catId] = {}
    }

    // Two-pass resolution:
    // Pass 1: resolves all basic formulas chronologically. RESTE_ variables might be incomplete for early months.
    // Pass 2: resolves again. Since Pass 1 filled resolvedMonths for the whole year, getResteValue now sees the accurate annual total!
    for (let pass = 1; pass <= 2; pass++) {
      for (let m = 1; m <= 12; m++) {
        for (const catId of evalOrder) {
          const cat = catIndex[catId]
          if (!cat) continue

          const isOverride = cat.monthOverrides[m] ?? false
          const stored     = cat.months[m] ?? null

          if (isOverride || !cat.formula) {
            resolvedMonths[catId][m] = stored !== null ? Number(stored) : null
          } else {
            // Callback to lookup past values across months and years
            const getResolvedValue = (targetCatId: number, offset: number): number | null => {
              let targetMonth = m + offset
              let targetYear  = year

              while (targetMonth <= 0) {
                targetMonth += 12
                targetYear -= 1
              }

              if (targetYear === year) {
                // If we are looking ahead in the same year, and it's pass 1, it might be undefined.
                // But in pass 2, it will have the value from pass 1.
                return resolvedMonths[targetCatId]?.[targetMonth] ?? null
              } else {
                return resolvedByYear[targetYear]?.[targetCatId]?.[targetMonth] ?? null
              }
            }

            // Helper to get RESTE for current or past years
            const getResteValue = (type: 'urssaf' | 'ir' | 'is', yearOffset: number): number => {
              const targetYear = year + yearOffset
              let tns = 0, urssafPaid = 0, taxable = 0, irPaid = 0
              let isProfit = 0, isPaid = 0

              if (targetYear === year) {
                // Current year: use currently resolving data (which includes full year data during Pass 2)
                for (const c of allCategories) {
                  for (let mm = 1; mm <= 12; mm++) {
                    let val = 0
                    if (resolvedMonths[c.id] && resolvedMonths[c.id][mm] !== undefined) {
                      val = resolvedMonths[c.id][mm] ?? 0
                    } else if (c.monthOverrides[mm]) {
                      val = Number(c.months[mm] ?? 0)
                    }
                    if (c.type === 'expense' && c.isTnsRemuneration) tns += val
                    if (c.type === 'expense' && c.isUrssafPayment) urssafPaid += val
                    if (c.type === 'income' && c.isTaxable) taxable += val
                    if (c.type === 'expense' && c.isTaxPayment && c.accountType === 'personal') irPaid += val
                    
                    if (c.corporateTaxDirection === 'income') isProfit += val
                    if (c.corporateTaxDirection === 'expense') isProfit -= val
                    if (c.type === 'expense' && c.isCorporateTaxPayment) isPaid += val
                  }
                }
              } else {
                // Past year: use already resolved data
                const pastData = resolvedByYear[targetYear]
                if (pastData) {
                  for (const c of allCategories) {
                    for (let mm = 1; mm <= 12; mm++) {
                      const val = pastData[c.id]?.[mm] ?? 0
                      if (c.type === 'expense' && c.isTnsRemuneration) tns += val
                      if (c.type === 'expense' && c.isUrssafPayment) urssafPaid += val
                      if (c.type === 'income' && c.isTaxable) taxable += val
                      if (c.type === 'expense' && c.isTaxPayment && c.accountType === 'personal') irPaid += val
                      
                      if (c.corporateTaxDirection === 'income') isProfit += val
                      if (c.corporateTaxDirection === 'expense') isProfit -= val
                      if (c.type === 'expense' && c.isCorporateTaxPayment) isPaid += val
                    }
                  }
                }
              }

              if (type === 'urssaf') {
                const strategy = taxStrategies.value[targetYear]
                const urssafRate = strategy?.urssafRate ?? 0.45
                const estim = tns * urssafRate
                return Math.round(Math.max(0, estim - urssafPaid) * 100) / 100
              } else if (type === 'ir') {
                const estim = calculateIncomeTaxSync(taxable, targetYear, taxStrategies.value)
                return Math.round(Math.max(0, estim - irPaid) * 100) / 100
              } else {
                const estim = calculateCorporateTaxSync(isProfit, targetYear, taxStrategies.value)
                return Math.round(Math.max(0, estim - isPaid) * 100) / 100
              }
            }

            const contextVars = {
              'MOIS': m,
            }

            const formulaWithCurrentResolved = cat.formula.replace(/\{cat:current/gi, `{cat:${catId}`)
            resolvedMonths[catId][m] = evaluateFormula(formulaWithCurrentResolved, getResolvedValue, getResteValue, contextVars)
          }
        }
      }
    }

    return resolvedMonths
  }

  /**
   * Computes monthly totals (income/expense/balance) from resolved values.
   */
  function computeMonthlyTotals(
    categories: BudgetCategoryData[],
    resolvedMonths: Record<number, Record<number, number | null>>,
  ): Record<number, { income: number; expense: number; balance: number }> {
    const totals: Record<number, { income: number; expense: number; balance: number }> = {}

    for (let m = 1; m <= 12; m++) {
      let income = 0
      let expense = 0

      for (const cat of categories) {
        const val = resolvedMonths[cat.id]?.[m] ?? 0
        if (cat.type === 'income') income += val
        else if (cat.type === 'expense') expense += val
      }

      totals[m] = { income, expense, balance: income - expense }
    }

    return totals
  }

  /**
   * Computes annual totals for one account.
   */
  function computeAnnualTotals(
    monthlyTotals: Record<number, { income: number; expense: number; balance: number }>,
  ): { income: number; expense: number; balance: number } {
    let income  = 0
    let expense = 0

    for (let m = 1; m <= 12; m++) {
      income  += monthlyTotals[m]?.income  ?? 0
      expense += monthlyTotals[m]?.expense ?? 0
    }

    return { income, expense, balance: income - expense }
  }

  /**
   * Computes the chained opening balances for an account.
   * If a month has isManual = true, its stored amount is used as the base.
   * Otherwise, the balance is carried over from the previous month.
   */
  function computeOpeningBalances(
    storedBalances: Record<number, { amount: number | null; isManual: boolean }>,
    monthlyTotals: Record<number, { income: number; expense: number; balance: number }>,
    previousYearCarry: number | null = null,
  ): Record<number, { amount: number | null; isManual: boolean }> {
    const result: Record<number, { amount: number | null; isManual: boolean }> = {}
    let carry: number | null = previousYearCarry

    for (let m = 1; m <= 12; m++) {
      const stored = storedBalances[m]

      if (stored?.isManual && stored.amount !== null) {
        // User-forced value: reset the carry chain from this month
        carry     = stored.amount
        result[m] = { amount: stored.amount, isManual: true }
      } else if (carry !== null) {
        // Carry from previous month: add previous month's balance (skip for m=1, unless carry is from previous year)
        if (m > 1) {
          carry = carry + (monthlyTotals[m - 1]?.balance ?? 0)
        }
        result[m] = { amount: carry, isManual: false }
      } else {
        result[m] = { amount: null, isManual: false }
      }
    }

    return result
  }

  return {
    extractCategoryIds,
    buildDependencyGraph,
    topologicalSort,
    evaluateFormula,
    resolveAllYears,
    computeMonthlyTotals,
    computeAnnualTotals,
    computeOpeningBalances,
  }
}

export type ResolvedBudgetData = Record<number, Record<number, number | null>>
